/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import axios from 'axios';
import { SecurityHelper } from 'sarsaparilla';
import { of, from } from 'rxjs';
import { map, concatMap, catchError, toArray, merge, filter } from 'rxjs/operators';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import omit from 'lodash/omit';
import set from 'lodash/set';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import values from 'lodash/values';
import { gnoment } from 'shared-ui/gnoment';

import { updateAccount } from 'ui-account/src/actions/profileActions';
import { printBlob } from 'ui-permit/src/utils/utilsNew';

import * as types from '../constants';
import { issuanceStatuses, salesChannelTypes } from '../utils/permitUtils';
import * as enrichment from '../utils/dataUtils';
import { VEHICLE_PERMIT_DATE_FORMAT } from '../utils/utils';

// Permit Actions
export function prepareIssuanceForCreate(issuanceObj, account = {}) {
    // ensure start_date is gnoment and utc offset
    const primary_permit_holder = {
        first_name: account.first_name,
        last_name: account.last_name,
        home_address: {
            street: [account.home_address.address1, account.home_address.address2],
            city: account.home_address.city,
            state_code: account.home_address.state,
            postal_code: account.home_address.zip_code,
        },
        email: account.email,
        phone: account.cell_phone,
    };
    const id = `${issuanceObj.permit_type}_${issuanceObj.division_id}`;
    const issuance = { id, primary_permit_holder, ...issuanceObj };

    issuance.sales_channel_type = salesChannelTypes.get(types.ONLINE);
    issuance.company_name = get(account, 'company_name', '');
    issuance.mailing_address = {};
    return issuance;
}

export function prepareIssuanceForUpdate(issuanceObj) {
    return issuanceObj;
}

export function formValidationChange(path, error) {
    return (dispatch) => {
        dispatch({
            type: types.SET_VALIDATION_ERROR,
            payload: { path, error },
        });
    };
}

export function fetchAreasFromPemrit(permit) {
    return (dispatch) => {
        const uniq = uniqBy(values(permit.divisions), 'district');
        const divisions = uniq.map((div) => get(div, 'district'));
        dispatch({
            type: types.SET_DISTRICTS,
            payload: divisions,
        });
    };
}

export function fetchPermit(permitId) {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/vehiclepermits/${permitId}`)
            .then((success) => {
                const permit = enrichment.transformPermit(
                    cloneDeep(success.data.payload)
                );
                dispatch({
                    type: types.GET_PERMIT,
                    payload: permit,
                });
                dispatch(fetchAreasFromPemrit(permit));
                return Promise.resolve(permit);
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_PERMIT_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                    });
                }
                throw error;
            });
    };
}

export function fetchSeasons(permitId) {
    return (dispatch) => {
        const currentDate = gnoment().format(VEHICLE_PERMIT_DATE_FORMAT);
        return axios
            .get(`${process.env.API}/permitseason/display/${permitId}/${currentDate}`)
            .then((success) => {
                dispatch({
                    type: types.GET_SEASONS,
                    payload: success.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_SEASONS_ERR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                    });
                }
            });
    };
}

export function postIssuance(issuance, doRedirect) {
    return (dispatch) =>
        axios
            .post(
                `${process.env.API}/vehiclepermits/${issuance.permit_id}/issuances`,
                JSON.stringify(issuance),
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({ type: types.POST_ISSUANCE, payload: response.data.payload });
                if (doRedirect) {
                    window.location.assign('/cart');
                }
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.POST_ISSUANCE_ERROR,
                        payload: 'error while posting issuance data',
                        error: error.response.data,
                    });
                }
            });
}

export function cancelIssuance(issuanceId, permitId) {
    // Note: do not actually need the body of the request (right now contains issuanceId)
    return (dispatch) =>
        axios
            .post(
                `${process.env.API}/vehiclepermits/${permitId}/issuances/${issuanceId}/cancel`,
                JSON.stringify(issuanceId),
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({ type: types.CANCEL_ISSUANCE, payload: response.data.payload });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.CANCEL_ISSUANCE_ERROR,
                        payload: 'error while cancelling reservation',
                        error: error.response.data,
                    });
                }
            });
}

export const createPendingReservations = (items) => {
    return (dispatch, getState) => {
        const account = getState().auth?.account?.account;
        if (!account) return;

        const list = items.map((item) => prepareIssuanceForCreate(item, account));
        dispatch({ type: types.ISSUANCE_LIST_CREATED, payload: list });
    };
};

const putToIssuanceEndpoint = (issuance) =>
    from(
        axios.put(
            `${process.env.API}/vehiclepermits/${issuance.permit_id}/issuances/${issuance.id}`,
            JSON.stringify(issuance),
            { headers: { Authorization: SecurityHelper.getAuthHeader() } }
        )
    );

const postToIssuanceEndpoint = (issuance) =>
    from(
        axios.post(
            `${process.env.API}/vehiclepermits/${issuance.permit_id}/issuances`,
            JSON.stringify(issuance),
            { headers: { Authorization: SecurityHelper.getAuthHeader() } }
        )
    );

const buildUpdatedAccountFromIssuance = (issuance, account) => {
    const {
        first_name,
        last_name,
        home_address,
        email,
        phone: cell_phone,
    } = issuance.primary_permit_holder;
    const { city, postal_code, state_code, street } = home_address;
    return {
        ...account,
        first_name,
        last_name,
        home_address: {
            city,
            zip_code: postal_code,
            state: state_code,
            address1: street?.[0] ?? '',
            address2: street?.[1] ?? '',
        },
        email,
        cell_phone,
    };
};

export const getCallToIssuanceStream = (items, callAPI) => {
    return (dispatch, getState) => {
        const transformToPrmise = (iss) => {
            let resSteeam = from(callAPI(iss));

            if (get(iss, `primary_permit_holder.save_profile`, false)) {
                const { account } = getState().auth.account;
                const updatedAccount = buildUpdatedAccountFromIssuance(iss, account);
                resSteeam = resSteeam.pipe(
                    merge(from(dispatch(updateAccount(updatedAccount))))
                );
            }

            return resSteeam;
        };

        dispatch({ type: types.POST_ISSUANCE_START });
        const stream = of(...items).pipe(
            concatMap(transformToPrmise),
            filter((res) => res),
            map((res) => res.data.payload),
            toArray(),
            catchError((err) => of(err.response?.data ?? { error: '' }))
        );

        return stream;
    };
};

// Server-side MaxIssuanceBatchSize. The selection UI cannot currently produce
// this many issuances, so exceeding it means a caller built the batch wrong.
export const MAX_ISSUANCE_BATCH_SIZE = 10;

export const postIssuanceBatch = (items, { recaptchaToken, recaptchaVersion }) => {
    // permit_id is overwritten server-side from the path param, so sending it on
    // each element is discarded. Keep the payload honest about that.
    const issuances = items.map((item) => omit(item, ['permit_id']));

    return axios.post(
        `${process.env.API}/vehiclepermits/${items[0].permit_id}/issuances/multi`,
        JSON.stringify({
            issuances,
            recaptcha_token: recaptchaToken,
            recaptcha_version: recaptchaVersion,
        }),
        { headers: { Authorization: SecurityHelper.getAuthHeader() } }
    );
};

export const addItems = (
    items,
    { navigate = true, recaptchaToken = null, recaptchaVersion = 'V3' } = {}
) => {
    return async (dispatch, getState) => {
        if (!items?.length) {
            const payload = { error: 'at least one issuance is required' };
            dispatch({ type: types.POST_ISSUANCE_ERROR, payload });
            return payload;
        }
        if (items.length > MAX_ISSUANCE_BATCH_SIZE) {
            const payload = { error: 'invalid issuance batch size' };
            dispatch({ type: types.POST_ISSUANCE_ERROR, payload });
            return payload;
        }

        dispatch({ type: types.POST_ISSUANCE_START });

        try {
            const response = await postIssuanceBatch(items, {
                recaptchaToken,
                recaptchaVersion,
            });
            dispatch({ type: types.POST_ISSUANCE, payload: response.data.payload });

            const toSave = items.find(
                (iss) => iss.primary_permit_holder?.save_profile ?? false
            );
            if (toSave) {
                const { account } = getState().auth.account;
                await dispatch(
                    updateAccount(buildUpdatedAccountFromIssuance(toSave, account))
                );
            }

            if (navigate) window.location.assign('/cart');
            return true;
        } catch (error) {
            const data = error.response?.data ?? {};
            // Creation is sequential and stops at the first failure, so the
            // issuances that came back are the leading slice of what we sent.
            // A missing payload means we cannot tell what was persisted.
            const created = Array.isArray(data.payload) ? data.payload : null;
            const createdCount = created ? created.length : null;

            if (createdCount >= items.length) {
                // Everything was persisted despite the error - nothing to retry.
                dispatch({ type: types.POST_ISSUANCE, payload: created });
                if (navigate) window.location.assign('/cart');
                return true;
            }

            if (createdCount > 0) {
                // Drop the created issuances so a retry cannot duplicate them.
                dispatch({
                    type: types.ISSUANCE_LIST_CREATED,
                    payload: items.slice(createdCount),
                });
            }

            // Resolve with the error payload (not just false) so callers can
            // inspect it - e.g. to detect a reCAPTCHA challenge signal.
            const payload = { error: data.error ?? '', createdCount };
            dispatch({ type: types.POST_ISSUANCE_ERROR, payload });
            return payload;
        }
    };
};

export const createPreliminaryIssuance = (items, navigateCallback) => {
    return (dispatch, getState) => {
        const { account } = getState().auth.account;
        const resList = items.map((res) => {
            const primary_permit_holder = {
                first_name: account.first_name,
                last_name: account.last_name,
                home_address: {
                    street: [
                        account.home_address.address1,
                        account.home_address.address2,
                    ],
                    city: account.home_address.city,
                    state_code: account.home_address.state,
                    postal_code: account.home_address.zip_code,
                },
                email: account.email,
                phone: account.cell_phone,
            };
            res.primary_permit_holder = primary_permit_holder;
            res.company_name = get(account, 'company_name', '');
            return res;
        });

        dispatch(getCallToIssuanceStream(resList, postToIssuanceEndpoint)).subscribe(
            (result) => {
                if (isArray(result)) {
                    const permitId = get(result, '[0].permit_id');
                    const issuanceId = get(result, '[0].id');
                    if (permitId && issuanceId) {
                        if (
                            result[0].status ===
                            issuanceStatuses.get(types.PERMIT_IN_PROGRESS)
                        ) {
                            window.location.assign('/cart');
                            return;
                        }
                        dispatch({ type: types.POST_ISSUANCE, payload: result });

                        navigateCallback({ permitId, issuanceId });
                    }
                } else {
                    dispatch({ type: types.POST_ISSUANCE_ERROR, payload: result });
                }
            }
        );
    };
};

export const editItems = (items, { navigate = true } = {}) => {
    return (dispatch) => {
        return new Promise((resolve) => {
            dispatch(getCallToIssuanceStream(items, putToIssuanceEndpoint)).subscribe(
                (result) => {
                    if (isArray(result)) {
                        if (navigate) window.location.assign('/cart');
                        resolve(true);
                    } else {
                        dispatch({ type: types.POST_ISSUANCE_ERROR, payload: result });
                        resolve(false);
                    }
                }
            );
        });
    };
};

export const updatePendingReservation = (id, path, value) => {
    return (dispatch, getState) => {
        const { list } = getState().vehiclepermit.pendingReservations;
        const updatingObj = cloneDeep(list.find((res) => res.id === id));
        const newObj = set(updatingObj, path, value);
        const payload = sortBy(
            [...list.filter((iss) => iss.id !== id), newObj],
            'permit_type'
        );
        dispatch({ type: types.ISSUANCE_LIST_CREATED, payload });
    };
};

export const purgePendingReservations = () => {
    return (dispatch) => dispatch({ type: types.CLEAR_ISSUANCE_LIST });
};

export const clearValidationErrors = () => {
    return (dispatch) => dispatch({ type: types.CLEAR_DISPLAY_UI_VALIDATION });
};

export function sendCurrentDate(date) {
    return (dispatch) => {
        return dispatch({
            type: types.SEND_CURRENT_DATE,
            payload: date,
        });
    };
}

//Used for account reservation items
export function fetchAllIssuances(issuanceId, permitId) {
    const baseURL = `${process.env.API}/vehiclepermits/${permitId}/issuances/${issuanceId}`;
    const url = baseURL;
    return (dispatch) => {
        return axios
            .get(url, { headers: { Authorization: SecurityHelper.getAuthHeader() } })
            .then((success) => {
                dispatch({
                    type: types.GET_ALL_ISSUANCES,
                    payload: Object.assign(success.data.payload),
                });
                return success;
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_ALL_ISSUANCES_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                        error_code: error.response.status,
                    });
                }
                throw error;
            });
    };
}

export function fetchIssuance(issuanceId, permitId, getUnpaid = false) {
    const baseURL = `${process.env.API}/vehiclepermits/${permitId}/issuances/${issuanceId}`;
    const unpaidURL = `${process.env.API}/vehiclepermits/${permitId}/issuances/${issuanceId}/unpaid`;

    const url = getUnpaid ? unpaidURL : baseURL;
    return (dispatch) => {
        return axios
            .get(url, { headers: { Authorization: SecurityHelper.getAuthHeader() } })
            .then((success) => {
                dispatch({
                    type: types.GET_ISSUANCE,
                    payload: Object.assign(success.data.payload),
                });
                return success;
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_ISSUANCE_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                        error_code: error.response.status,
                    });
                }
                throw error;
            });
    };
}

export const fetchRecarea = (recareaId) => {
    return (dispatch) => {
        return axios
            .get(
                `${process.env.API}/search?fq=entity_type%3Arecarea&fq=entity_id%3A${recareaId}`
            )
            .then((success) => {
                if (success.data && success.data.size > 0) {
                    dispatch({
                        type: types.GET_RECAREA,
                        payload: Object.assign(success.data.results),
                    });
                } else {
                    const error = 'No recreation area found';
                    dispatch({ type: types.GET_RECAREA_ERROR, payload: error, error });
                }
            })
            .catch(() => {
                const error = 'Error while fetching recarea data';
                dispatch({ type: types.GET_RECAREA_ERROR, payload: error, error });
            });
    };
};

export const fetchCart = () => {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/cart/shoppingcart`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then((response) => {
                dispatch({ type: types.GET_CART, payload: response.data });
            })
            .catch(() => {
                dispatch({
                    type: types.GET_CART_ERROR,
                    payload: 'error fetching cart',
                    error: true,
                });
            });
    };
};

export const cancelReservation = (reservationId) => {
    return (dispatch) => {
        return axios
            .delete(`${process.env.API}/cart/shoppingcart/${reservationId}`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then((response) => {
                dispatch({ type: types.CANCEL_RESERVATION, payload: response.data });
                dispatch(fetchCart());
                // This used to be history from the legacy react router, but since this function doesn't seem to be used we replaced it with window.history
                window.history.back();
            })
            .catch(() => {
                dispatch({
                    type: types.CANCEL_RESERVATION_ERROR,
                    payload: 'error canceling reservation',
                    error: true,
                });
            });
    };
};

export const printIssuancePDF = (permitId, issuanceId) => {
    return (dispatch) => {
        return axios
            .get(
                `${process.env.API}/vehiclepermits/${permitId}/issuances/${issuanceId}/pdf`,
                {
                    headers: { Authorization: SecurityHelper.getAuthHeader() },
                    responseType: 'arraybuffer',
                }
            )
            .then((response) => {
                const blob = new Blob([response.data], { type: 'application/pdf' });
                printBlob(blob);
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_ISSUANCE_PDF_ERROR,
                        payload: 'error printing issuance PDF',
                        error: error.response.data,
                    });
                }
            });
    };
};

export function calculateFeeDifference(issuance) {
    const preparedIssuance = prepareIssuanceForUpdate(issuance);

    return (dispatch) =>
        axios
            .post(
                `${process.env.API}/vehiclepermits/${preparedIssuance.permit_id}/issuances/${preparedIssuance.id}/fees`,
                JSON.stringify(preparedIssuance),
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({
                    type: types.POST_ISSUANCE_CALCULATE_FEES,
                    payload: response.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.POST_ISSUANCE_CALCULATE_FEES_ERROR,
                        payload: 'error while calculating fee difference',
                        error: error.response.data,
                    });
                }
            });
}

export const createPendingReservationsFromExisting = () => {
    return (dispatch, getState) => {
        const { issuance } = getState().vehiclepermit.reservation;
        const list = [cloneDeep(issuance)];
        dispatch({ type: types.ISSUANCE_LIST_CREATED, payload: list });
    };
};

export function clearIssuanceFromStore() {
    return (dispatch) => {
        dispatch({ type: types.CLEAR_ISSUANCE });
    };
}

export function displayValidationErrors() {
    return (dispatch) =>
        dispatch({
            type: types.DISPLAY_UI_VALIDATIONS,
        });
}

export function setPermitType(value) {
    return {
        type: types.SET_PERMIT_TYPE,
        permitType: value,
    };
}

export function setPermitDate(value) {
    return {
        type: types.SET_PERMIT_DATE,
        permitDate: value,
    };
}

export function setSelectedDivision(division) {
    return {
        type: types.SET_SELECTED_DIVISION,
        division,
    };
}

export function setSelectedStartDate(value) {
    return {
        type: types.SET_SELECTED_START_DATE,
        date: value,
    };
}

export function setSelectedEndDate(value) {
    return {
        type: types.SET_SELECTED_END_DATE,
        date: value,
    };
}

export function setActiveTab(value) {
    return {
        type: types.SET_ACTIVE_TAB,
        activeTab: value,
    };
}

export function setDivisionSearchCriteria(criteria) {
    return {
        type: types.SET_DIV_SEARCH_CRITERIA,
        divisionSearchCriteria: criteria,
    };
}

export const fetchUnpaidIssuance = (issuanceId, permitId) => {
    return (dispatch) => {
        return axios
            .get(
                `${process.env.API}/vehiclepermits/${permitId}/issuances/${issuanceId}/unpaid`,
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((success) => {
                dispatch({
                    type: types.GET_ISSUANCE,
                    payload: Object.assign(success.data.payload),
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_ISSUANCE_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                        error_code: error.response.status,
                    });
                }
            });
    };
};

export function verifyAlternate(permitId, issuanceId) {
    return (dispatch) =>
        axios
            .get(
                `${process.env.API}/vehiclepermits/${permitId}/issuances/${issuanceId}/alternate/verify`,
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({
                    type: types.VERIFY_ALTERNATE,
                    payload: response.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.VERIFY_ALTERNATE_ERROR,
                        payload: 'error while verifying alternate permit holder',
                        error: error.response.data,
                    });
                }
            });
}

export function markAlertAsRead(alertId = '-1') {
    return axios.put(
        `${process.env.API}/vehiclepermits/account/alerts/${alertId}/read`,
        {},
        { headers: { Authorization: SecurityHelper.getAuthHeader() } }
    );
}

export function confirmAlternate(permitId, issuanceId, isAccepted, account, alertId) {
    return (dispatch) =>
        axios
            .post(
                `${process.env.API}/vehiclepermits/${permitId}/issuances/${issuanceId}/alternate/confirm?accepted=${isAccepted}`,
                JSON.stringify(account),
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({
                    type: types.CONFIRM_ALTERNATE,
                    payload: response.data.payload,
                });
                markAlertAsRead(alertId);
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.CONFIRM_ALTERNATE_ERROR,
                        payload: 'error while confirming alternate permit holder',
                        error: error.response.data,
                    });
                }
            });
}

export function fetchPermitReservationAlert(reservationId) {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/vehiclepermits/account/alerts/unread`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then(({ data }) => {
                const alerts = data.payload;
                if (alerts) {
                    const al = alerts.find(
                        ({ is_read: isRead, alert_body: alertBody }) =>
                            !isRead && alertBody?.href.indexOf(reservationId) > 0
                    );
                    dispatch({
                        type: types.GET_PERMIT_RESERVATION_ALERT,
                        payload: al,
                    });
                }
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_PERMIT_RESERVATION_ALERT_ERROR,
                        payload: 'error while getting reservation alert',
                        error: error.response.data,
                    });
                }
            });
    };
}
